import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Button — pastel-palette variants with AA-passing contrast.
 *
 * primary    : lavender bg + dark text (8.6:1 AAA)
 * secondary  : neutral surface + ink-700 text
 * ghost      : transparent + ink-700, hover lavender-50
 * success    : mint bg + emerald-900 text
 * warning    : peach bg + amber-900 text
 * danger     : rose bg + rose-900 text
 *
 * Sizes: sm (32px), md (40px), lg (48px).
 *
 * Supports `asChild` (radix Slot) so we can render a Link with button styles
 * without nested elements. Typical usage:
 *   <Button asChild><Link to="/foo">Go</Link></Button>
 */
const buttonVariants = cva(
  cn(
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    'font-medium transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lavender-400 focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
    'rounded-md',
  ),
  {
    variants: {
      variant: {
        primary:
          'bg-lavender-200 text-lavender-900 hover:bg-lavender-300 active:bg-lavender-300/90',
        secondary:
          'bg-ink-100 text-ink-800 hover:bg-ink-200 border border-ink-200',
        ghost:
          'bg-transparent text-ink-700 hover:bg-lavender-50',
        success:
          'bg-mint-200 text-mint-900 hover:bg-mint-300',
        warning:
          'bg-peach-200 text-peach-900 hover:bg-peach-300',
        danger:
          'bg-rose-200 text-rose-900 hover:bg-rose-300',
        link:
          'bg-transparent text-lavender-700 underline underline-offset-2 hover:text-lavender-900 px-0 py-0 h-auto',
      },
      size: {
        sm: 'h-8 px-3 text-sm',
        md: 'h-10 px-4 text-sm',
        lg: 'h-12 px-6 text-base',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref as never}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';
